/**
 * Etiquetas que el BACKEND envía en `/api/admin/marketing/metadata` y nombres
 * de experiencia del catálogo (`shared/config/experiences/catalog.json`).
 *
 * Van en su propio fragmento porque su origen es distinto al del resto: no son
 * literales escritos en las vistas del admin, son DATOS que llegan por HTTP y
 * que la UI pinta. El endpoint es ciego al idioma —no acepta locale ni lee
 * `Accept-Language`— y sus valores técnicos (`trigger.value`,
 * `experience.code`, `field.name`) son los que se persisten: acá solo se
 * localiza lo que el humano lee, en el punto donde se pinta.
 *
 * Clave = el español EXACTO tal como sale del backend, byte a byte. Varias
 * etiquetas de origen están sin acentuar ('Deposito', 'Periodico',
 * 'Notificacion') y una ya venía en portugués ('Níveis de perda…'): la clave
 * copia el literal de origen, no su ortografía correcta. Corregir el backend
 * sin corregir estas claves rompería la traducción en silencio, que es
 * justamente lo que la prueba de completitud impide.
 *
 * Consumidor único: `etiquetaDeMetadataTraducida()` /
 * `textoDeMetadataTraducido()` en js/services/etiqueta-de-metadata-traducida.js
 */

export const DICCIONARIO_METADATA_MARKETING = {
    // ── Triggers ────────────────────────────────────────────────────────────
    Deposito: { pt: 'Depósito', en: 'Deposit', ru: 'Депозит' },
    'Referido registrado': { pt: 'Indicado cadastrado', en: 'Referral registered', ru: 'Реферал зарегистрирован' },
    'Referido deposita': { pt: 'Indicado deposita', en: 'Referral deposits', ru: 'Реферал вносит депозит' },
    Periodico: { pt: 'Periódico', en: 'Recurring', ru: 'Периодически' },
    'Instala la app': { pt: 'Instala o app', en: 'Installs the app', ru: 'Устанавливает приложение' },
    'Visita (B2B)': { pt: 'Visita (B2B)', en: 'Visit (B2B)', ru: 'Визит (B2B)' },
    Manual: { pt: 'Manual', en: 'Manual', ru: 'Вручную' },

    // ── Facts (constructor de condiciones) ──────────────────────────────────
    'Es primera recarga': { pt: 'É a primeira recarga', en: 'Is first deposit', ru: 'Первое пополнение' },
    'Total depositado': { pt: 'Total depositado', en: 'Total deposited', ru: 'Всего внесено' },
    'Jugado total': { pt: 'Total apostado', en: 'Total wagered', ru: 'Всего поставлено' },
    'Total ganado': { pt: 'Total ganho', en: 'Total won', ru: 'Всего выиграно' },
    'Cantidad de apuestas': { pt: 'Quantidade de apostas', en: 'Number of bets', ru: 'Количество ставок' },
    'Dias desde registro': { pt: 'Dias desde o cadastro', en: 'Days since registration', ru: 'Дней с регистрации' },
    'Dias consecutivos jugando': {
        pt: 'Dias consecutivos jogando',
        en: 'Consecutive days playing',
        ru: 'Дней подряд в игре',
    },
    'Referidos totales': { pt: 'Indicados totais', en: 'Total referrals', ru: 'Всего рефералов' },
    'Referidos activos': { pt: 'Indicados ativos', en: 'Active referrals', ru: 'Активные рефералы' },
    'Es influencer': { pt: 'É influenciador', en: 'Is influencer', ru: 'Инфлюенсер' },
    'Dias inactivo': { pt: 'Dias inativo', en: 'Days inactive', ru: 'Дней без активности' },
    'Monto del evento': { pt: 'Valor do evento', en: 'Event amount', ru: 'Сумма события' },
    'Tiene la app instalada': { pt: 'Tem o app instalado', en: 'Has the app installed', ru: 'Приложение установлено' },
    'Dias sin entrar por la app': {
        pt: 'Dias sem entrar pelo app',
        en: 'Days without opening the app',
        ru: 'Дней без входа через приложение',
    },
    'Dias con visita (B2B)': { pt: 'Dias com visita (B2B)', en: 'Days with a visit (B2B)', ru: 'Дней с визитом (B2B)' },
    'Dias seguidos con visita (B2B)': {
        pt: 'Dias seguidos com visita (B2B)',
        en: 'Consecutive days with a visit (B2B)',
        ru: 'Дней подряд с визитом (B2B)',
    },
    'Dias sin visitar (B2B)': { pt: 'Dias sem visitar (B2B)', en: 'Days without a visit (B2B)', ru: 'Дней без визита (B2B)' },
    'Entra desde la app ahora': {
        pt: 'Entra pelo app agora',
        en: 'Entering from the app now',
        ru: 'Сейчас входит из приложения',
    },

    // ── Tipos de recompensa y sus campos ────────────────────────────────────
    'Bonus fijo': { pt: 'Bônus fixo', en: 'Fixed bonus', ru: 'Фиксированный бонус' },
    'Bonus porcentaje': { pt: 'Bônus percentual', en: 'Percentage bonus', ru: 'Процентный бонус' },
    'Calcular desde': { pt: 'Calcular a partir de', en: 'Calculate from', ru: 'Рассчитывать от' },
    'Ultimo saldo cero': { pt: 'Último saldo zero', en: 'Last zero balance', ru: 'С последнего нулевого баланса' },
    'Ultimos N dias': { pt: 'Últimos N dias', en: 'Last N days', ru: 'Последние N дней' },
    Dias: { pt: 'Dias', en: 'Days', ru: 'Дни' },
    'Máximo 1 cashback por día por cliente': {
        pt: 'Máximo 1 cashback por dia por cliente',
        en: 'Maximum 1 cashback per day per customer',
        ru: 'Максимум 1 кэшбэк в день на клиента',
    },
    // El origen ya viene en portugués mezclado con español; la clave lo copia tal cual.
    'Níveis de perda (opcional — si se usan, pisan el % plano)': {
        pt: 'Níveis de perda (opcional — se usados, substituem o % fixo)',
        en: 'Loss tiers (optional — if used, they override the flat %)',
        ru: 'Уровни проигрыша (необязательно — заменяют фиксированный %)',
    },
    'Activar filon dorado': { pt: 'Ativar filão dourado', en: 'Activate golden vein', ru: 'Активировать золотую жилу' },
    '% Apuesta filon': { pt: '% Aposta do filão', en: 'Vein bet %', ru: '% ставки жилы' },
    'Duracion (meses)': { pt: 'Duração (meses)', en: 'Duration (months)', ru: 'Длительность (месяцы)' },
    'Bonus multinivel': { pt: 'Bônus multinível', en: 'Multi-level bonus', ru: 'Многоуровневый бонус' },
    'Monto base': { pt: 'Valor base', en: 'Base amount', ru: 'Базовая сумма' },
    Niveles: { pt: 'Níveis', en: 'Levels', ru: 'Уровни' },
    Arbol: { pt: 'Árvore', en: 'Tree', ru: 'Дерево' },
    'Bonus equipo': { pt: 'Bônus de equipe', en: 'Team bonus', ru: 'Командный бонус' },
    'Pool total': { pt: 'Pool total', en: 'Total pool', ru: 'Общий пул' },
    'Periodo (dias)': { pt: 'Período (dias)', en: 'Period (days)', ru: 'Период (дни)' },
    Metrica: { pt: 'Métrica', en: 'Metric', ru: 'Метрика' },
    'Min. participantes': { pt: 'Mín. participantes', en: 'Min. participants', ru: 'Мин. участников' },

    // ── Operadores y tipos de hito ──────────────────────────────────────────
    Si: { pt: 'Sim', en: 'Yes', ru: 'Да' },
    '>=': { pt: '>=', en: '>=', ru: '>=' },
    '<=': { pt: '<=', en: '<=', ru: '<=' },
    '=': { pt: '=', en: '=', ru: '=' },
    '>': { pt: '>', en: '>', ru: '>' },
    '<': { pt: '<', en: '<', ru: '<' },
    'Dias consecutivos': { pt: 'Dias consecutivos', en: 'Consecutive days', ru: 'Дней подряд' },
    'Dias jugados': { pt: 'Dias jogados', en: 'Days played', ru: 'Дней в игре' },

    // ── Nombres de las experiencias del catálogo ────────────────────────────
    'Lobby clásico': { pt: 'Lobby clássico', en: 'Classic lobby', ru: 'Классическое лобби' },
    'Aviso Toast': { pt: 'Aviso Toast', en: 'Toast notice', ru: 'Уведомление Toast' },
    'Rueda de cashback': { pt: 'Roda de cashback', en: 'Cashback wheel', ru: 'Колесо кэшбэка' },
    'Celebración de bonus': { pt: 'Celebração de bônus', en: 'Bonus celebration', ru: 'Празднование бонуса' },
    'Celebración de premio': { pt: 'Celebração de prêmio', en: 'Prize celebration', ru: 'Празднование приза' },
    'Tarjeta de recompensa': { pt: 'Cartão de recompensa', en: 'Reward card', ru: 'Карта награды' },
    'Progreso de wagering': { pt: 'Progresso de rollover', en: 'Wagering progress', ru: 'Прогресс отыгрыша' },
    'Menú del jugador': { pt: 'Menu do jogador', en: 'Player menu', ru: 'Меню игрока' },
    'Mis promociones': { pt: 'Minhas promoções', en: 'My promotions', ru: 'Мои акции' },
    'Cofres de premio': { pt: 'Baús de prêmio', en: 'Prize chests', ru: 'Сундуки с призами' },
    'Cartas de premio': { pt: 'Cartas de prêmio', en: 'Prize cards', ru: 'Призовые карты' },
    'Cofre imperial': { pt: 'Baú imperial', en: 'Imperial chest', ru: 'Императорский сундук' },
    'Cofres elementales': { pt: 'Baús elementais', en: 'Elemental chests', ru: 'Сундуки стихий' },
    'Cámara del tesoro': { pt: 'Câmara do tesouro', en: 'Treasure vault', ru: 'Сокровищница' },
    'Carta dorada': { pt: 'Carta dourada', en: 'Golden card', ru: 'Золотая карта' },
    'Cartas por niveles': { pt: 'Cartas por níveis', en: 'Tiered cards', ru: 'Карты по уровням' },
    'Mazo misterioso': { pt: 'Baralho misterioso', en: 'Mystery deck', ru: 'Таинственная колода' },
};
